import React, { useState, useEffect } from 'react';
import {
  Info, X, Check, ArrowRight, Trash, Tray,
} from '@phosphor-icons/react';
import {
  getbranchnames,
  getCommencedGroupCodes,
  getCommencedGroupTickets,

  getCommencedChequeDetails,
  saveData,
  deleteCheques,
} from '../api/connectionRegistryApi';
import { useLoginName } from '../utils/auditUser';
import SearchableSelect from './SearchableSelect';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import SectionLock from './SectionLock';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

function StatusTag({ status }) {
  const commenced = status === 'Commenced';
  return (
    <span
      style={{
        fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '999px',
        color: commenced ? '#065F46' : '#374151',
        background: commenced ? '#ECFDF5' : '#F3F4F6',
        border: `1px solid ${commenced ? '#A7F3D0' : '#E5E7EB'}`,
      }}
    >
      {status}
    </span>
  );
}

function PdChequeRemoveForm() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [branchlist, setBranchlist] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [chequeRecords, setChequeRecords] = useState([]);
  const [selectedCheques, setSelectedCheques] = useState([]);

  const [cao, setCao] = useState('');
  const [group, setGroup] = useState('');
  const [ticket, setTicket] = useState('');
  const [touched, setTouched] = useState(false);

  const [reason, setReason] = useState('');

  const [branchschema, setBranchschema] = useState(null);
  const [branchCode, setBranchCode] = useState(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingCheques, setPendingCheques] = useState([]);

  useEffect(() => {
    getbranchnames().then((res) => setBranchlist(res || [])).catch(() => setBranchlist([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const resetFromTicket = () => {
    setChequeRecords([]);
    setSelectedCheques([]);
    setReason('');
  };

  const resetFromGroup = () => {
    setTicket('');
    setTickets([]);
    resetFromTicket();
  };

  const resetFromBranch = () => {
    setBranchschema(null);
    setBranchCode(null);
    setGroup('');
    setGroupList([]);
    resetFromGroup();
  };

  const handleBranchChange = async (branchName) => {
    setCao(branchName);
    resetFromBranch();
    if (!branchName) return;

    const selected = branchlist.find((x) => x.branchName === branchName);
    if (!selected) return;

    const schema = selected.branchCode;
    setBranchschema(schema);
    setBranchCode(schema);

    try {
      const res = await getCommencedGroupCodes(schema, schema);
      setGroupList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setGroupList([]);
    }
  };

  const handleGroupChange = async (groupcode) => {
    setGroup(groupcode);
    resetFromGroup();
    if (!groupcode) return;

    try {
      const res = await getCommencedGroupTickets(branchschema, groupcode);
      setTickets(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setTickets([]);
    }
  };

  const handleTicketChange = async (ticketno) => {
    setTicket(ticketno);
    resetFromTicket();
    if (!ticketno) return;

    try {
      const res = await getCommencedChequeDetails(branchschema, group, branchCode, ticketno);
      setChequeRecords(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
      setSelectedCheques([]);
    } catch {
      setChequeRecords([]);
      setSelectedCheques([]);
    }
  };

  const isRowSelected = (row) => selectedCheques.some(
    (c) => c.tbl_trans_ps_cheques_id === row.tbl_trans_ps_cheques_id,
  );

  const toggleRow = (row) => {
    if (isRowSelected(row)) {
      setSelectedCheques((prev) => prev.filter((c) => c.tbl_trans_ps_cheques_id !== row.tbl_trans_ps_cheques_id));
    } else {
      setSelectedCheques((prev) => [...prev, row]);
    }
  };

  const toggleAll = () => {
    if (selectedCheques.length === chequeRecords.length) {
      setSelectedCheques([]);
    } else {
      setSelectedCheques([...chequeRecords]);
    }
  };

  const caoInvalid = touched && !cao;
  const groupInvalid = touched && !group;
  const ticketInvalid = touched && !ticket;
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);

  /**
   * Step 1: guard on selection (delete button is already disabled until
   * at least one row is checked — this is just a safety-net) then open
   * the confirm modal. Only the checked rows are staged; the rest of the
   * grid is left untouched.
   */
  const confirmDelete = () => {
    setTouched(true);

    if (selectedCheques.length === 0) {
      showToast('warn', 'No Selection', 'Please select at least one cheque record to delete.');
      return;
    }

    if (reason.trim().length < 10 || reason.length > 200) {
      showToast('warn', 'Reason Required', 'Please provide a reason (10-200 characters) for this deletion.');
      return;
    }

    setPendingCheques([...selectedCheques]);
    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (isSaving) return;
    setShowConfirmModal(false);
  };

  const handleDeleteSuccess = (deletedIds) => {
    setChequeRecords((prev) => prev.filter((c) => !deletedIds.includes(c.tbl_trans_ps_cheques_id)));
    setSelectedCheques([]);
    setPendingCheques([]);
    setIsSaving(false);
    showToast('success', 'Success', 'Audit saved and cheque(s) deleted successfully.', true);
    handleClear();
  };

  /**
   * Step 2: fires only after user clicks "Yes, Save" in the modal.
   * Same audit-first-then-delete sequence as before.
   */
  const doSave = async () => {
    if (pendingCheques.length === 0) return;

    setShowConfirmModal(false);
    setIsSaving(true);

    const chequesToDelete = [...pendingCheques];
    const chequenumbers = chequesToDelete.map((c) => c.cheque_number).join(',');
    const chequeids = chequesToDelete.map((c) => c.tbl_trans_ps_cheques_id).join(',');
    const deletedIds = chequesToDelete.map((c) => c.tbl_trans_ps_cheques_id);

    const audit = {
      schemaName: branchschema,
      loginName,
      loginTime: new Date().toISOString(),
      changeType: 'PD_CHEQUE_REMOVE',
      oldData: chequesToDelete,
      newData: { status: 'DELETED' },
      reason: reason.trim() || 'PD Cheque removed',
    };

    try {
      await saveData([audit]);

      try {
        await deleteCheques(branchschema, chequenumbers, chequeids);
        handleDeleteSuccess(deletedIds);
      } catch (err) {
        // API returns plain text "Success" — some HTTP clients treat this as a parse error
        const isSuccess = err?.response?.data?.text?.toLowerCase?.() === 'success'
          || err?.response?.data?.toLowerCase?.() === 'success';

        if (isSuccess) {
          handleDeleteSuccess(deletedIds);
        } else {
          setIsSaving(false);
          console.error('Delete failed:', err);
          showToast('warn', 'Partial Success', 'Audit saved, but delete failed. Please try again.', true);
        }
      }
    } catch (err) {
      setIsSaving(false);
      console.error('Audit save failed:', err);
      showToast('error', 'Audit Save Failed', 'Audit save failed. Delete was not performed.', true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
    setTouched(false);
  };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="pd-cheque-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="pd-cheque-form">
        {isSaving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Trash size={18} color="#002FA7" />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Cheque Management</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>PD Cheque Remove</div>
          </div>
        </div>

        {/* SECTION A: Select Ticket */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Ticket</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group code and ticket to load cheque records</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              CAO <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchlist}
              getLabel={(o) => o.branchName}
              getValue={(o) => o.branchName}
              value={cao}
              onChange={handleBranchChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={caoInvalid}
              testId="pd-cao-select"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>CAO is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Group Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={groupList}
              getLabel={(o) => o.groupcode}
              getValue={(o) => o.groupcode}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group"
              emptyMessage="Select a CAO first"
              disabled={!branchschema}
              error={groupInvalid}
              testId="pd-group-select"
              renderOption={(item) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <span>{item.groupcode}</span>
                  <StatusTag status={item.chitgroup_status} />
                </div>
              )}
            />
            {groupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Ticket No <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`ticket-${formKey}`}
              options={tickets}
              getLabel={(o) => String(o.ticketno)}
              getValue={(o) => o.ticketno}
              value={ticket}
              onChange={handleTicketChange}
              placeholder="Select ticket"
              emptyMessage="Select a group code first"
              disabled={!group}
              error={ticketInvalid}
              testId="pd-ticket-select"
            />
            {ticketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Ticket No is required</div>}
          </div>
        </div>

        {(chequeRecords.length > 0 || ticket) && (
          <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />
        )}

        {/* SECTION B: Cheque Records */}
        {chequeRecords.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Cheque Records</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Select one or more records to remove</div>
              </div>
            </div>

            {selectedCheques.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#002FA7', background: '#EFF4FF', padding: '4px 10px', borderRadius: '999px' }}>
                  {selectedCheques.length} record(s) selected
                </span>
              </div>
            )}

            <div style={{ overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
              <table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse', fontSize: '13px' }} data-testid="pd-cheque-table">
                <thead>
                  <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                    <th style={{ width: '4rem', textAlign: 'center', padding: '10px 12px' }}>
                      <input
                        type="checkbox"
                        checked={selectedCheques.length === chequeRecords.length && chequeRecords.length > 0}
                        onChange={toggleAll}
                        data-testid="pd-select-all"
                      />
                    </th>
                    <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: '#6B7280' }}>Cheque No</th>
                    <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: '#6B7280' }}>Bank</th>
                    <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: '#6B7280' }}>Branch</th>
                  </tr>
                </thead>
                <tbody>
                  {chequeRecords.map((row) => {
                    const rowSelected = isRowSelected(row);
                    return (
                      <tr
                        key={row.tbl_trans_ps_cheques_id}
                        style={{ borderBottom: '1px solid #F3F4F6', background: rowSelected ? '#EFF4FF' : 'transparent' }}
                      >
                        <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                          <input
                            type="checkbox"
                            checked={rowSelected}
                            onChange={() => toggleRow(row)}
                            data-testid={`pd-cheque-checkbox-${row.tbl_trans_ps_cheques_id}`}
                          />
                        </td>
                        <td style={{ padding: '10px 12px', fontFamily: 'monospace' }}>{row.cheque_number}</td>
                        <td style={{ padding: '10px 12px' }}>{row.bank_name}</td>
                        <td style={{ padding: '10px 12px' }}>{row.branch_name}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* SECTION C: Justification */}
            <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />
            <SectionLock locked={selectedCheques.length === 0} hint="Select at least one cheque record in section B first">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Justification</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Provide a reason for removing the selected cheque(s)</div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
              <span style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
                {reason.length} / 200
              </span>
            </div>
            <textarea
              className="input-field"
              style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
              rows={4}
              maxLength={200}
              placeholder="Enter the reason for removing the selected cheque(s)…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="pd-cheque-reason"
            />
            {reasonInvalid && (
              <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
                {reason.trim().length === 0 ? 'Required' : reason.trim().length < 10 ? 'Minimum 10 characters' : 'Maximum 200 characters'}
              </div>
            )}
            </SectionLock>
          </div>
        )}

        {/* Empty state */}
        {ticket && chequeRecords.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '30px', color: '#9CA3AF' }}>
            <Tray size={28} />
            <div style={{ fontSize: '13px' }}>No cheque records found for the selected ticket.</div>
          </div>
        )}

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={isSaving} data-testid="pd-clear-button">
              Clear
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              disabled={isSaving || selectedCheques.length === 0}
              data-testid="pd-delete-button"
              style={{
                padding: '8px 16px', fontSize: '13px', fontWeight: '600', color: 'white',
                background: (isSaving || selectedCheques.length === 0) ? '#F3A6A6' : '#DC2626',
                border: 'none', borderRadius: '6px',
                cursor: (isSaving || selectedCheques.length === 0) ? 'not-allowed' : 'pointer',
              }}
            >
              <Trash size={14} style={{ marginRight: '4px' }} />
              {isSaving ? 'Deleting...' : selectedCheques.length > 0 ? `Delete (${selectedCheques.length})` : 'Delete'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="pd-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Trash size={18} color="#DC2626" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Cheque Deletion</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px', maxHeight: '50vh', overflowY: 'auto' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                Do you want to delete the selected cheque(s)? {pendingCheques.length} record(s) will be removed.
              </p>

              {pendingCheques.map((cheque) => (
                <div key={cheque.tbl_trans_ps_cheques_id} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Cheque No.</p>
                    <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{cheque.cheque_number} ({cheque.bank_name})</p>
                  </div>
                  <ArrowRight size={14} color="#9CA3AF" />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Status</p>
                    <p style={{ fontSize: '13px', color: '#DC2626', fontWeight: '600', margin: 0 }}>Deleted</p>
                  </div>
                </div>
              ))}

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px', marginBottom: '10px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{reason.trim()}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={isSaving} className="btn-secondary" data-testid="pd-confirm-cancel">
                Cancel
              </button>
              <button
                type="button"
                onClick={doSave}
                disabled={isSaving}
                data-testid="pd-confirm-delete"
                style={{
                  padding: '8px 16px', fontSize: '13px', fontWeight: '600', color: 'white',
                  background: isSaving ? '#F3A6A6' : '#DC2626', border: 'none', borderRadius: '6px',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                }}
              >
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PdChequeRemoveForm;